import { useTranslation } from 'react-i18next'
import { clsx } from 'clsx'

import { WizardHeader as GivingWizardHeader } from '@percent/domain/giving'
import { Box, Button, Icon, Text, Tooltip } from '@percent/lemonade'
import { useIsMobile } from '@percent/workplace-giving/common/hooks/useIsMobile/useIsMobile'
import type { WizardHeaderProps } from './WizardHeader.types'
import classes from './WizardHeader.module.scss'

function CompactActionButton({
  label,
  onClick,
  variant,
  isDisabled,
  isLoading,
  disabledTooltipCopy,
  testId
}: Readonly<{
  label?: string
  onClick: VoidFunction
  variant: 'primary' | 'secondary'
  isDisabled: boolean
  isLoading: boolean
  disabledTooltipCopy: string
  testId: string
}>) {
  const button = (
    <Button
      size="small"
      shape="oval"
      variant={variant}
      onClick={onClick}
      disabled={isDisabled}
      loading={isLoading}
      data-testid={testId}
    >
      {label}
    </Button>
  )

  if (isDisabled && !isLoading) {
    return (
      <Tooltip content={disabledTooltipCopy}>
        <div className={classes.DisabledButtonWrapper}>{button}</div>
      </Tooltip>
    )
  }

  return button
}

function CompactWizardHeader({
  title,
  className,
  onCancel,
  cancelBtnLabel,
  cancelBtnTestId = 'cancelBtn',
  onPrimaryBtn,
  primaryBtnLabel,
  isPrimaryBtnDisabled = false,
  isPrimaryBtnLoading = false,
  primaryBtnDisabledTooltipCopy,
  primaryBtnTestId = 'publishBtn',
  onSecondaryBtn,
  secondaryBtnLabel,
  isSecondaryBtnDisabled = false,
  isSecondaryBtnLoading = false,
  secondaryBtnDisabledTooltipCopy,
  secondaryBtnTestId = 'wizard-header-secondary-btn'
}: Readonly<
  WizardHeaderProps &
    Required<
      Pick<WizardHeaderProps, 'cancelBtnLabel' | 'primaryBtnDisabledTooltipCopy' | 'secondaryBtnDisabledTooltipCopy'>
    >
>) {
  return (
    <Box className={clsx(classes.Compact, className)}>
      {onCancel && (
        <button
          type="button"
          className={classes.CloseButton}
          aria-label={cancelBtnLabel}
          onClick={onCancel}
          data-testid={cancelBtnTestId}
        >
          <span aria-hidden="true" className={classes.CloseIcon}>
            <Icon name="close" size={4} color="inherit" />
          </span>
        </button>
      )}
      <Text className={classes.Title}>{title}</Text>
      <Box className={classes.Actions}>
        {onSecondaryBtn && (
          <CompactActionButton
            label={secondaryBtnLabel}
            onClick={onSecondaryBtn}
            variant="secondary"
            isDisabled={isSecondaryBtnDisabled}
            isLoading={isSecondaryBtnLoading}
            disabledTooltipCopy={secondaryBtnDisabledTooltipCopy}
            testId={secondaryBtnTestId}
          />
        )}
        <CompactActionButton
          label={primaryBtnLabel}
          onClick={onPrimaryBtn}
          variant="primary"
          isDisabled={isPrimaryBtnDisabled}
          isLoading={isPrimaryBtnLoading}
          disabledTooltipCopy={primaryBtnDisabledTooltipCopy}
          testId={primaryBtnTestId}
        />
      </Box>
    </Box>
  )
}

export function WizardHeader({
  primaryBtnLabel,
  primaryBtnDisabledTooltipCopy,
  secondaryBtnDisabledTooltipCopy,
  cancelBtnLabel,
  ...props
}: Readonly<WizardHeaderProps>) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const resolvedProps = {
    ...props,
    primaryBtnLabel: primaryBtnLabel ?? t('workplace_giving.common.publish'),
    primaryBtnDisabledTooltipCopy: primaryBtnDisabledTooltipCopy ?? t('workplace_giving.validation.formError'),
    secondaryBtnDisabledTooltipCopy: secondaryBtnDisabledTooltipCopy ?? t('workplace_giving.validation.formError'),
    cancelBtnLabel: cancelBtnLabel ?? t('workplace_giving.common.cancel')
  }

  if (isMobile) {
    return <CompactWizardHeader {...resolvedProps} />
  }

  return <GivingWizardHeader {...resolvedProps} />
}
